import React, { useState, useEffect, useRef } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

const HorizontalLine = ({ className }: { className?: string }) => {
  return (
    <div className={`absolute w-full h-[1px] ${className} z-10`}>
      <svg
        width="1260"
        height="1"
        viewBox="0 0 1260 1"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full"
      >
        <path d="M0 1L1260 1.00011" stroke="url(#paint0_linear_2344_127091)" />
        <defs>
          <linearGradient
            id="paint0_linear_2344_127091"
            x1="-4.37114e-08"
            y1="1.5"
            x2="1260"
            y2="1.49998"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#EAEAEA" stopOpacity="0.1" />
            <stop
              offset="0.511666"
              stopColor="#BCBCBC"
              stopOpacity="0.543334"
            />
            <stop offset="1" stopColor="#EAEAEA" stopOpacity="0.1" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

const VerticalLine = () => {
  return (
    <div className="absolute h-[87.5px] -right-1 -top-[4.5px] z-10">
      <svg
        viewBox="0 0 7 93"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full"
      >
        <path d="M3.46094 0V93" stroke="url(#paint0_linear_2344_127125)" />
        <path d="M3.46094 10V16" stroke="#FF622B" />
        <path d="M6.92188 13L-0.00120187 13" stroke="#FF622B" />
        <path d="M3.46094 78V84" stroke="#FF622B" />
        <path d="M6.92188 81L-0.00120187 81" stroke="#FF622B" />
        <defs>
          <linearGradient
            id="paint0_linear_2344_127125"
            x1="1.96112"
            y1="-8.00011"
            x2="13.4608"
            y2="85.5"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#EAEAEA" stopOpacity="0" />
            <stop
              offset="0.532358"
              stopColor="#B4B4B4"
              stopOpacity="0.467642"
            />
            <stop offset="1" stopColor="#848484" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export const TrustedBy = () => {
  const logos = useStaticQuery(graphql`
    query trustedLogos {
      allFile(
        filter: { absolutePath: { regex: "/trusted-logos/" } }
        sort: { name: ASC }
      ) {
        nodes {
          publicURL
          name
        }
      }
    }
  `);

  const [activeRowIndex, setActiveRowIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const logoItems = logos.allFile.nodes;
  // Create three sets of logos
  const tripleLogoItems = [...logoItems, ...logoItems, ...logoItems];

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;

      const scrollY = window.scrollY;
      const scrollStep = window.innerWidth >= 1024 ? 150 : // desktop
                        window.innerWidth >= 768 ? 120 : // tablet
                        75; // mobile

      const newRowIndex = Math.floor(scrollY / scrollStep) % logoItems.length;

      if (newRowIndex !== activeRowIndex) {
        setActiveRowIndex(newRowIndex);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeRowIndex, logoItems.length]);

  return (
    <div
      className="sticky -bottom-2 h-20 overflow-hidden py-2 z-20"
      ref={containerRef}
    >
      <div className="relative h-full overflow-hidden">
        <HorizontalLine className="top-0" />
        <div className="absolute w-full">
          <div className="relative">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 overflow-hidden bg-white">
              {tripleLogoItems.map((logo, logoIndex) => (
                <div
                  key={logoIndex}
                  className="col-span-1 relative flex items-center justify-center"
                  style={{
                    transform: `translateY(${-activeRowIndex * 64}px)`,
                    transition: 'transform 1000ms ease-in-out',
                  }}
                >
                  <img
                    src={logo.publicURL}
                    alt={logo.name}
                    className="h-full max-h-16 max-w-42 object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <HorizontalLine className="bottom-0" />
      </div>
      <div className="absolute top-0 left-0 w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 z-10">
        {logoItems.slice(0, 5).map((_, index) => (
          <div key={index} className="col-span-1 relative">
            <VerticalLine />
          </div>
        ))}
      </div>
    </div>
  );
};
